<script setup lang="ts">
import type { Diagnostic } from "@/types";
import { computed, ref } from "vue";
import { useTranslation } from "i18next-vue";
import { useDeleteManyDiagnosticImages } from "@/stores/queries";
import {
  useSortReportImageToSection,
  useExcludeReportImage,
  useCreateReportImage,
} from "@/stores/queries/report-images.query";
import { useMergedReportImages } from "@/composable/useMergedReportImages";
import type { MergedReportImage } from "@/types/report-images.types";
import { useDragItem } from "../findings/useDragItem";
import { useMarqueeSelection } from "@/composable/useMarqueeSelection";
import ReportState from "@/stores/report/ReportState";
import PhotoGalleryItem from "./PhotoGalleryItem.vue";
import MoveSectionModal from "./MoveSectionModal.vue";
import EditReportImageDialog from "./EditReportImageDialog.vue";
import PhotoSelectionActionBar from "./PhotoSelectionActionBar.vue";
import DRDisplayList from "@/components/UI/items/DRDisplayList.vue";

const props = defineProps<{
  diagnostic: Diagnostic;
  reportId: number;
  selectedSectionId?: number | null;
}>();

const { t } = useTranslation();
const diagnosticId = computed(() => props.diagnostic.id);
const reportId = computed(() => props.reportId);

// Use merged images to get ReportImage status
const { mergedImages } = useMergedReportImages(diagnosticId, reportId);

const { mutate: sortToSection } = useSortReportImageToSection(reportId);
const { mutate: excludeImage } = useExcludeReportImage(reportId);
const { mutateAsync: createReportImage } = useCreateReportImage(reportId);
const { mutate: deleteManyImages, isPending: isDeletingMany } =
  useDeleteManyDiagnosticImages(diagnosticId);

const { onDragStart } = useDragItem("image");

// Selection state - stores diagnosticImageIds
const selectedImages = ref<Set<number>>(new Set());

const toggleSelection = (diagnosticImageId: number, event: MouseEvent) => {
  event.stopPropagation();
  if (selectedImages.value.has(diagnosticImageId)) {
    selectedImages.value.delete(diagnosticImageId);
  } else {
    selectedImages.value.add(diagnosticImageId);
  }
};

const isSelected = (diagnosticImageId: number) =>
  selectedImages.value.has(diagnosticImageId);

const clearSelection = () => {
  selectedImages.value.clear();
};

// Helper to get or create reportImageId for a merged image
const ensureReportImageId = async (
  image: MergedReportImage,
): Promise<number | null> => {
  if (image.reportImageId) return image.reportImageId;
  if (image.isVirtual) {
    // Create the ReportImage first
    const newReportImage = await createReportImage({
      diagnosticImageId: image.diagnosticImageId,
      reportId: props.reportId,
    });
    return newReportImage.id;
  }
  return null;
};

// Bulk actions
const showBulkMoveDialog = ref(false);
const bulkMoveToSection = async (sectionId: number | null) => {
  for (const diagnosticImageId of selectedImages.value) {
    const image = mergedImages.value?.find(
      (img) => img.diagnosticImageId === diagnosticImageId,
    );
    if (!image) continue;
    const reportImageId = await ensureReportImageId(image);
    if (reportImageId) {
      sortToSection({ imageId: reportImageId, sectionId });
    }
  }
  clearSelection();
};

const bulkExclude = async () => {
  for (const diagnosticImageId of selectedImages.value) {
    const image = mergedImages.value?.find(
      (img) => img.diagnosticImageId === diagnosticImageId,
    );
    if (!image) continue;
    const reportImageId = await ensureReportImageId(image);
    if (reportImageId) {
      excludeImage(reportImageId);
    }
  }
  clearSelection();
};

const bulkDelete = () => {
  const ids = [...selectedImages.value];
  deleteManyImages(ids, {
    onSuccess: () => {
      clearSelection();
    },
  });
};

// Filter state - only when no section is selected
type StatusFilter = "all" | "UNPROCESSED" | "SORTED" | "EXCLUDED";
const statusFilter = ref<StatusFilter>("UNPROCESSED");

// Check if filtering by section
const isFilteringBySection = computed(() => props.selectedSectionId != null);

// Sort images by diagnosticImageId to maintain stable order across re-fetches
const sortedImages = computed(() => {
  if (!mergedImages.value) return [];
  return [...mergedImages.value].sort(
    (a, b) => a.diagnosticImageId - b.diagnosticImageId,
  );
});

const filteredImages = computed(() => {
  let images = sortedImages.value;

  // Filter by section if selected
  if (isFilteringBySection.value) {
    images = images.filter(
      (img) => img.sortedSectionId === props.selectedSectionId,
    );
  }

  // Then filter by status
  if (statusFilter.value !== "all") {
    images = images.filter((img) => {
      // SORTED filter includes both SORTED and PLACED (has findings)
      if (statusFilter.value === "SORTED") {
        return img.status === "SORTED" || img.status === "PLACED";
      }
      return img.status === statusFilter.value;
    });
  }

  return images;
});

const statusCounts = computed(() => {
  if (!mergedImages.value)
    return { all: 0, UNPROCESSED: 0, SORTED: 0, EXCLUDED: 0 };
  const counts = {
    all: mergedImages.value.length,
    UNPROCESSED: 0,
    SORTED: 0,
    EXCLUDED: 0,
  };
  mergedImages.value.forEach((img) => {
    if (img.status === "UNPROCESSED") counts.UNPROCESSED++;
    // SORTED count includes both SORTED and PLACED (has findings)
    else if (img.status === "SORTED" || img.status === "PLACED")
      counts.SORTED++;
    else if (img.status === "EXCLUDED") counts.EXCLUDED++;
  });
  return counts;
});

const filterOptions: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "report:photos.filter.all" },
  { id: "UNPROCESSED", label: "report:photos.filter.unprocessed" },
  { id: "SORTED", label: "report:photos.filter.sorted" },
  { id: "EXCLUDED", label: "report:photos.filter.ignored" },
];

// Custom drag image for multi-selection
const createMultiDragImage = (count: number): HTMLElement => {
  const container = document.createElement("div");
  container.style.cssText = `
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 2px solid var(--bs-primary, #0d6efd);
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #333;
    pointer-events: none;
    position: fixed;
    top: -1000px;
    left: -1000px;
    z-index: 9999;
  `;

  // Icon
  const icon = document.createElement("div");
  icon.style.cssText = `
    width: 32px;
    height: 32px;
    background: var(--bs-primary, #0d6efd);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 16px;
  `;
  icon.innerHTML = `<svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
    <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
    <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z"/>
  </svg>`;

  // Text
  const text = document.createElement("span");
  text.textContent = t("report:photos.dragMultiple", { count });

  container.appendChild(icon);
  container.appendChild(text);

  return container;
};

const handleDragStart = (image: MergedReportImage, event: DragEvent) => {
  const isMultiSelect =
    selectedImages.value.has(image.diagnosticImageId) &&
    selectedImages.value.size > 1;

  // Determine how many items are being dragged
  const dragCount = isMultiSelect ? selectedImages.value.size : 1;

  if (isMultiSelect) {
    event.dataTransfer?.setData(
      "selectedIds",
      JSON.stringify([...selectedImages.value]),
    );
  }

  // Always create custom drag image
  const dragImage = createMultiDragImage(dragCount);
  document.body.appendChild(dragImage);

  // Set the drag image with offset
  event.dataTransfer?.setDragImage(dragImage, 40, 25);

  // Clean up after drag ends
  const cleanup = () => {
    dragImage.remove();
    event.target?.removeEventListener("dragend", cleanup);
  };
  event.target?.addEventListener("dragend", cleanup);

  // Pass the diagnosticImageId for drag tracking
  onDragStart({ id: image.diagnosticImageId } as any, event);
};

const imageMinWidth = computed(() => {
  return ReportState.getPhotoWidth("gallery");
});

// Edit dialog state
const editingImage = ref<MergedReportImage | null>(null);
const showEditDialog = ref(false);

const openEditDialog = (image: MergedReportImage) => {
  editingImage.value = image;
  showEditDialog.value = true;
};

// Marquee selection
const galleryContentRef = ref<HTMLElement | null>(null);
const marqueeHoveredIds = ref<Set<number>>(new Set());

const { isSelecting, selectionRect } = useMarqueeSelection({
  containerRef: galleryContentRef,
  itemSelector: "[data-gallery-item-id]",
  itemIdAttribute: "data-gallery-item-id",
  onSelectionChange: (ids) => {
    marqueeHoveredIds.value = new Set(ids);
  },
  onSelectionEnd: (ids) => {
    // Add all selected items to the selection
    ids.forEach((id) => {
      selectedImages.value.add(id);
    });
    marqueeHoveredIds.value = new Set();
  },
});

const isMarqueeHovered = (imageId: number) =>
  marqueeHoveredIds.value.has(imageId);

// Expose clearSelection for parent component
defineExpose({ clearSelection });
</script>

<template>
  <div class="photos-gallery h-100 w-100 d-flex flex-column">
    <!-- Header with filters -->
    <div class="gallery-header px-4 py-2 border-bottom bg-white">
      <!-- Status filter pills -->
      <div class="d-flex gap-1">
        <button
          v-for="filter in filterOptions"
          :key="filter.id"
          class="filter-pill px-2 py-1 border-0 rounded"
          :class="{ active: statusFilter === filter.id }"
          @click="statusFilter = filter.id"
        >
          {{ t(filter.label) }}
          <span class="filter-count">({{ statusCounts[filter.id] }})</span>
        </button>
      </div>
    </div>

    <!-- Selection action bar -->
    <PhotoSelectionActionBar
      v-if="selectedImages.size > 0"
      :selected-count="selectedImages.size"
      :is-deleting="isDeletingMany"
      @move="showBulkMoveDialog = true"
      @exclude="bulkExclude"
      @delete="bulkDelete"
      @clear="clearSelection"
    />

    <!-- Gallery content -->
    <div
      ref="galleryContentRef"
      class="gallery-content flex-grow-1 p-4"
      style="overflow-y: auto"
      :class="{ 'is-selecting': isSelecting }"
    >
      <!-- Loading overlay for bulk delete -->
      <div v-if="isDeletingMany" class="deleting-overlay">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Marquee selection rectangle -->
      <div
        v-if="isSelecting"
        class="marquee-selection"
        :style="{
          left: `${selectionRect.x}px`,
          top: `${selectionRect.y}px`,
          width: `${selectionRect.width}px`,
          height: `${selectionRect.height}px`,
        }"
      ></div>

      <DRDisplayList
        :data="filteredImages"
        key-index="diagnosticImageId"
        :min-width="imageMinWidth"
        display="grid"
      >
        <template #item="{ item: image }">
          <div
            :data-gallery-item-id="image.diagnosticImageId"
            class="gallery-item-wrapper h-100"
            :class="{
              'marquee-hovered': isMarqueeHovered(image.diagnosticImageId),
            }"
          >
            <PhotoGalleryItem
              class="h-100"
              :image="image"
              :diagnostic-id="diagnostic.id"
              :report-id="reportId"
              :selected="isSelected(image.diagnosticImageId)"
              @toggle-selection="
                toggleSelection(image.diagnosticImageId, $event)
              "
              @drag-start="handleDragStart(image, $event)"
              @action-performed="clearSelection"
              @edit="openEditDialog(image)"
            />
          </div>
        </template>
        <template #empty>
          <div>
            <div class="text-grey fw-normal">
              <slot name="empty"> </slot>
              <template v-if="!$slots.empty">
                <div
                  class="py-4 mt-4 h-100 d-flex flex-column align-items-center justify-content-center text-muted"
                >
                  <el-icon size="64" class="mb-3"
                    ><i class="bi-images"></i
                  ></el-icon>
                  <p>{{ t("report:photos.empty") }}</p>
                </div>
              </template>
            </div>
          </div>
        </template></DRDisplayList
      >
    </div>
  </div>

  <!-- Move to section dialog (bulk) -->
  <MoveSectionModal
    v-model="showBulkMoveDialog"
    :report-id="reportId"
    @select="bulkMoveToSection"
  />

  <!-- Edit report image dialog -->
  <EditReportImageDialog
    v-model="showEditDialog"
    :image="editingImage"
    :report-id="reportId"
  />
</template>

<style lang="scss" scoped>
.photos-gallery {
  background-color: var(--bs-light);
  min-width: 0;
  flex: 1;
}

.gallery-header {
  flex-shrink: 0;
}

.gallery-content {
  position: relative;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
}

.deleting-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.filter-pill {
  background: transparent;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(.active) {
    background-color: rgba(0, 0, 0, 0.05);
  }

  &.active {
    background-color: var(--el-color-primary-light-9);
    color: var(--bs-primary);
  }
}

.filter-count {
  opacity: 0.6;
  font-size: 11px;
}

// Marquee selection styles
.gallery-content {
  &.is-selecting {
    cursor: crosshair;
    user-select: none;
  }
}

.marquee-selection {
  position: absolute;
  background-color: rgba(var(--bs-primary-rgb), 0.15);
  border: 2px solid var(--bs-primary);
  border-radius: 4px;
  pointer-events: none;
  z-index: 100;
}

.gallery-item-wrapper {
  position: relative;
  border-radius: 12px;
  transition: box-shadow 0.15s ease;

  &.marquee-hovered {
    &::after {
      content: "";
      position: absolute;
      inset: 0;
      background-color: rgba(var(--bs-primary-rgb), 0.25);
      border-radius: 12px;
      pointer-events: none;
      z-index: 5;
    }
  }
}
</style>
